import React, { useCallback, useEffect, useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from "@mui/material";
import { useFetchErrorHookSetter } from "./FetchProvider";
import { defineMessages, FormattedMessage, MessageDescriptor } from "react-intl";

const errorTranslations = defineMessages({
    internalTitle: {
        id: "internal_server_error.title",
        defaultMessage: "Internal server error",
    },
    internalMessage: {
        id: "internal_server_error.message",
        defaultMessage: "Please call the system administrator!",
    },
    unreachableTitle: {
        id: "unreachable_server_error.title",
        defaultMessage: "$app_name is not available",
    },
    unreachableMessage: {
        id: "unreachable_server_error.message",
        defaultMessage: "Please try again later!",
    },
});

type DialogTranslation = {
    title: MessageDescriptor,
    message: MessageDescriptor | string,
};
type DialogTranslationSetter = React.Dispatch<React.SetStateAction<DialogTranslation>>;

const handleUnknownError = (setDialogTranslation: DialogTranslationSetter, error: any) => {
    setDialogTranslation({
        title: errorTranslations.internalTitle,
        message: errorTranslations.internalMessage,
    });
    console.debug("An internal server error occured, but no json with 'message' field came back", error);
};

/**
 * This component listens for errors from FetchProvider and RequestActionCreator, and displays a dialog
 * with the error message.
 * */
const BackendErrorDialog = () => {
    const [show, setShow] = useState(false);
    const [dialogTranslation, setDialogTranslation] = useState<DialogTranslation>({
        title: errorTranslations.internalTitle,
        message: errorTranslations.internalMessage,
    });

    const setErrorHook = useFetchErrorHookSetter();

    /**
     * The error hook that gets called when an error occurs. It can handle Response, Error and InternalResponse types.
     * It sets the dialog title and message based on the error type.
     * If it encounters an unknown error type, it logs the error and rethrows it.
     * (maybe we should still show the dialog in that case)
     * */
    const errorHook = useCallback(
        async (error: Response | Error) => {
            const handleUnreachableServerError = () => {
                setDialogTranslation({
                    title: errorTranslations.unreachableTitle,
                    message: errorTranslations.unreachableMessage,
                });
                setShow(true);
            };

            console.debug("Error hook called", error);
            if (error instanceof Response) {
                if (error.status === 502 || error.status === 504) {
                    handleUnreachableServerError();
                    setShow(true);
                } else if (error.status >= 500) {
                    const data = await error.json();
                    const message = data.message;
                    if (message) {
                        setDialogTranslation({
                            title: errorTranslations.internalTitle,
                            message,
                        });
                    } else {
                        handleUnknownError(setDialogTranslation, error);
                    }
                    setShow(true);
                }
            } else if (error instanceof Error) {
                // It is safe to assume that the error has to do with not being able to reach the server
                // so we will use the same method as it was an 502 or 504 error, but just in case
                // we will log the error so it might be easier to debug
                console.error("Error happened during fetch", error);
                handleUnreachableServerError();
            } else {
                console.error("Unknown error type", error);
                // eslint-disable-next-line @typescript-eslint/only-throw-error
                throw error;
            }
        },
        [setShow, setDialogTranslation],
    );

    useEffect(() => {
        setErrorHook(errorHook);
    }, [errorHook, setErrorHook]);

    return (
        <Dialog open={show} maxWidth="sm" fullWidth>
            <DialogTitle>
                <FormattedMessage {...dialogTranslation.title} />
            </DialogTitle>
            <DialogContent>
                {typeof dialogTranslation.message === "string" ? (
                    dialogTranslation.message
                ) : (
                    <FormattedMessage {...dialogTranslation.message} />
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => window.location.reload()} variant="contained" color="primary">
                    <FormattedMessage id="reload_page" defaultMessage="Reload page" />
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BackendErrorDialog;
